/**
 * Image limits for a lab's branding (contract 1.2 and 4.10), checked in the browser so the
 * editor can say what is wrong before an upload is sent.
 *
 * Same order and same rules as the server's `validate_image_bytes`: the type is read from
 * the bytes, never the file name, then the slot's cap, then the size from the PNG IHDR or
 * the JPEG frame header. **The server decides**: onboarding-api and the orchestrator both
 * check again, and an SVG renamed `.png` is refused there too.
 */

export type ImageSlot = "logo" | "stamp" | "master_signature" | "signature";
export type MediaType = "image/png" | "image/jpeg";

export const LOGO_MAX_BYTES = 262_144;
export const STAMP_MAX_BYTES = 262_144;
export const SIGNATURE_MAX_BYTES = 131_072;
export const IMAGE_MAX_EDGE_PX = 1_000;

export const SLOT_MAX_BYTES: Record<ImageSlot, number> = {
  logo: LOGO_MAX_BYTES,
  stamp: STAMP_MAX_BYTES,
  master_signature: SIGNATURE_MAX_BYTES,
  signature: SIGNATURE_MAX_BYTES,
};

/**
 * The most image bytes one delivery can carry: the logo, the stamp, the master signature
 * and the approving doctor's signature, each at its cap (plan B4 and B7).
 */
export const DELIVERY_IMAGE_BUDGET_BYTES =
  LOGO_MAX_BYTES + STAMP_MAX_BYTES + SIGNATURE_MAX_BYTES + SIGNATURE_MAX_BYTES;

export type ImageRefusal = "image_type_refused" | "image_too_large" | "image_dimensions";

export interface ImageCheck {
  mediaType: MediaType;
  byteSize: number;
  widthPx: number;
  heightPx: number;
}

const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_MAGIC = [0xff, 0xd8, 0xff];
// SOF0 to SOF15 carry the frame size. C4, C8 and CC share the range and are not frames.
const JPEG_SOF = new Set(
  Array.from({ length: 16 }, (_, i) => 0xc0 + i).filter((m) => ![0xc4, 0xc8, 0xcc].includes(m)),
);
// Markers with no length field: TEM, RST0 to RST7, SOI and EOI.
const JPEG_STANDALONE = new Set([0x01, 0xd0, 0xd1, 0xd2, 0xd3, 0xd4, 0xd5, 0xd6, 0xd7, 0xd8, 0xd9]);

/** The byte at `i`, or -1 past the end, so a short file reads as not matching. */
function at(data: Uint8Array, i: number): number {
  return data[i] ?? -1;
}

function startsWith(data: Uint8Array, magic: number[]): boolean {
  return data.length >= magic.length && magic.every((b, i) => data[i] === b);
}

/** `image/png` or `image/jpeg` by the magic bytes, else null. */
export function sniffMediaType(data: Uint8Array): MediaType | null {
  if (startsWith(data, PNG_MAGIC)) return "image/png";
  if (startsWith(data, JPEG_MAGIC)) return "image/jpeg";
  return null;
}

function u16(data: Uint8Array, i: number): number {
  return (at(data, i) << 8) | at(data, i + 1);
}

function u32(data: Uint8Array, i: number): number {
  return (
    ((at(data, i) << 24) >>> 0) + (at(data, i + 1) << 16) + (at(data, i + 2) << 8) + at(data, i + 3)
  );
}

function pngSize(data: Uint8Array): [number, number] | null {
  const ihdr = String.fromCharCode(...data.slice(12, 16));
  if (data.length < 33 || ihdr !== "IHDR" || u32(data, 8) !== 13) return null;
  return [u32(data, 16), u32(data, 20)];
}

function jpegSize(data: Uint8Array): [number, number] | null {
  let index = 2;
  const end = data.length;
  while (index < end) {
    if (at(data, index) !== 0xff) return null;
    while (index < end && at(data, index) === 0xff) index += 1;
    if (index >= end) break;
    const marker = at(data, index);
    index += 1;
    if (JPEG_STANDALONE.has(marker)) {
      if (marker === 0xd9) break;
      continue;
    }
    if (index + 2 > end) break;
    const length = u16(data, index);
    if (length < 2 || index + length > end) break;
    if (JPEG_SOF.has(marker)) {
      if (length < 7) break;
      return [u16(data, index + 5), u16(data, index + 3)];
    }
    if (marker === 0xda) break;
    index += length;
  }
  return null;
}

/** Accept PNG or JPEG bytes within the slot's cap and 1 to 1,000 px a side, or say why not. */
export function checkImage(
  data: Uint8Array,
  slot: ImageSlot,
): { ok: true; image: ImageCheck } | { ok: false; refusal: ImageRefusal } {
  const mediaType = sniffMediaType(data);
  if (mediaType === null) return { ok: false, refusal: "image_type_refused" };
  if (data.length > SLOT_MAX_BYTES[slot]) return { ok: false, refusal: "image_too_large" };
  const size = mediaType === "image/png" ? pngSize(data) : jpegSize(data);
  if (size === null) return { ok: false, refusal: "image_type_refused" };
  const [widthPx, heightPx] = size;
  if (
    !(
      widthPx >= 1 &&
      widthPx <= IMAGE_MAX_EDGE_PX &&
      heightPx >= 1 &&
      heightPx <= IMAGE_MAX_EDGE_PX
    )
  ) {
    return { ok: false, refusal: "image_dimensions" };
  }
  return { ok: true, image: { mediaType, byteSize: data.length, widthPx, heightPx } };
}

/** A byte count as people read it: `512 B`, `12.5 KB`, `256 KB`. KB is 1,024 bytes here. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return `${Number.isInteger(kb) ? kb : kb.toFixed(1)} KB`;
}

/** The bytes of a picked file. `File.arrayBuffer` is missing in older test DOMs. */
export async function fileBytes(file: Blob): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === "function") {
    return new Uint8Array(await file.arrayBuffer());
  }
  return new Uint8Array(
    await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error ?? new Error("could not read the file"));
      reader.readAsArrayBuffer(file);
    }),
  );
}
